import type { CollectionScoringStatus } from '@vizit/api';

/*
 * What the collection page tells the user about scorings, as a pure state machine: which scorings it has seen run
 * (under any benchmark, so one that lands after the user moved on is still announced), which uploads it waits on
 * (one batch per benchmark, since only that benchmark's list shows the API's answer), and the notices to show. The
 * hook around it feeds it observations and status results, and turns notices into toasts.
 */

/** A scoring as the page sees it: identity, run, name to announce and state. */
interface ObservedScoring {
  id: string;
  categoryId: string;
  name: string;
  status: CollectionScoringStatus;
  /** Server time of the current run; a scoring requested again keeps its id but gets a new time */
  requestedAt: string;
}

/** A scoring seen running, announced once it lands. */
interface TrackedScoring extends ObservedScoring {
  benchmarkKey: string;
}

/**
 * An upload into the collection under one benchmark. The API scores the new assets by requesting the benchmark's
 * scorings again, or inside a run already going; an image uploaded under a category is scored under that category's
 * audiences by the upload itself, and no run is requested for that pair. The batch is scored once its runs have
 * landed and its images carry a score under the pair on screen; it announces that once and stays quiet about each
 * run.
 */
interface UploadBatch {
  /** Every asset uploaded while the batch was open, less the ones whose upload failed */
  assetCount: number;
  /** Client time of the first upload, only to bound the wait */
  submittedAt: string;
  /** The newest run among the scorings at the first upload, server time: a later run answers the upload */
  baselineRequestedAt: string | null;
  /** The runs that score the new assets: running at upload, or requested since */
  scoringIds: string[];
  settledIds: string[];
  /** The images uploaded, as their records are created */
  imageIds: string[];
  /** Those of them scored under the pair on screen */
  scoredImageIds: string[];
  /**
   * Announced as scored. The batch stays until its window ends, claiming the runs the API still requests for the
   * upload (other pairs, after a categorized image's own scoring) so they land quietly
   */
  isAnnounced: boolean;
}

type CollectionScoringNotice =
  | { id: number; kind: 'ready'; benchmarkKey: string; categoryId: string; name: string }
  | { id: number; kind: 'uploadStarted'; assetCount: number }
  | { id: number; kind: 'uploadScored'; assetCount: number };

interface CollectionScoringNoticesState {
  tracked: Record<string, TrackedScoring>;
  /** Upload batches by benchmark key */
  uploads: Record<string, UploadBatch>;
  /** Runs the user requested from this page (`id@requestedAt`); they are announced on their own, never as uploads */
  requestedRuns: string[];
  outbox: CollectionScoringNotice[];
  nextNoticeId: number;
}

type CollectionScoringNoticesAction =
  /** The listed scorings of the benchmark on screen, after each read */
  | { type: 'observed'; benchmarkKey: string; scorings: ObservedScoring[] }
  /** Scorings the user just requested by adding categories */
  | { type: 'requested'; benchmarkKey: string; scorings: ObservedScoring[] }
  /** Assets uploaded while `scorings` were listed under the benchmark on screen */
  | { type: 'uploaded'; benchmarkKey: string; assetCount: number; submittedAt: string; scorings: ObservedScoring[] }
  /** The record of one uploaded image was created */
  | { type: 'imageUploaded'; benchmarkKey: string; imageId: string }
  /** These uploaded images now carry a score under the pair on screen */
  | { type: 'imagesScored'; benchmarkKey: string; imageIds: string[] }
  /** One upload of the batch failed, before or after its record was created */
  | { type: 'imageFailed'; benchmarkKey: string; imageId?: string }
  /** A tracked scoring's status route answered that it landed */
  | { type: 'settled'; id: string; status: Exclude<CollectionScoringStatus, 'pending'> }
  /** The wait for an upload ran out without every run landing */
  | { type: 'expired'; benchmarkKey: string; submittedAt: string }
  /** The notices up to this id were shown */
  | { type: 'delivered'; throughId: number };

const INITIAL_COLLECTION_SCORING_NOTICES: CollectionScoringNoticesState = {
  tracked: {},
  uploads: {},
  requestedRuns: [],
  outbox: [],
  nextNoticeId: 1,
};

const runKey = (scoring: Pick<ObservedScoring, 'id' | 'requestedAt'>) => `${scoring.id}@${scoring.requestedAt}`;

function batchOf(state: CollectionScoringNoticesState, scoringId: string) {
  return Object.entries(state.uploads).find(([, batch]) => batch.scoringIds.includes(scoringId));
}

/** A notice before it is numbered. */
type NoticeDraft = CollectionScoringNotice extends infer Notice
  ? Notice extends CollectionScoringNotice
    ? Omit<Notice, 'id'>
    : never
  : never;

function notify(state: CollectionScoringNoticesState, notice: NoticeDraft): CollectionScoringNoticesState {
  return {
    ...state,
    outbox: [...state.outbox, { ...notice, id: state.nextNoticeId } as CollectionScoringNotice],
    nextNoticeId: state.nextNoticeId + 1,
  };
}

/**
 * Announces a batch once its runs have landed and every image it expects has a record and a score under the pair on
 * screen. Records are created a few at a time, so the first images can score before the last ones exist.
 */
function completeBatch(state: CollectionScoringNoticesState, benchmarkKey: string): CollectionScoringNoticesState {
  const batch = state.uploads[benchmarkKey];
  if (!batch || batch.isAnnounced) {
    return state;
  }
  const runsDone = batch.scoringIds.every((id) => batch.settledIds.includes(id));
  const imagesDone =
    batch.imageIds.length >= batch.assetCount && batch.imageIds.every((id) => batch.scoredImageIds.includes(id));
  if (!runsDone || !imagesDone) {
    return state;
  }
  return notify(
    { ...state, uploads: { ...state.uploads, [benchmarkKey]: { ...batch, isAnnounced: true } } },
    { kind: 'uploadScored', assetCount: batch.assetCount },
  );
}

/** Records that a run landed for the batch it belongs to, and announces the batch once it is scored. */
function settleInBatch(state: CollectionScoringNoticesState, scoringId: string): CollectionScoringNoticesState {
  const entry = batchOf(state, scoringId);
  if (!entry) {
    return state;
  }
  const [benchmarkKey, batch] = entry;
  if (batch.settledIds.includes(scoringId)) {
    return state;
  }
  const settled = { ...batch, settledIds: [...batch.settledIds, scoringId] };
  return completeBatch({ ...state, uploads: { ...state.uploads, [benchmarkKey]: settled } }, benchmarkKey);
}

function observe(
  state: CollectionScoringNoticesState,
  benchmarkKey: string,
  scorings: ObservedScoring[],
): CollectionScoringNoticesState {
  let next = state;
  const newRuns = scorings.filter(
    (scoring) => scoring.status === 'pending' && next.tracked[scoring.id]?.requestedAt !== scoring.requestedAt,
  );
  if (newRuns.length > 0) {
    const tracked = { ...next.tracked };
    for (const scoring of newRuns) {
      tracked[scoring.id] = { ...scoring, benchmarkKey };
    }
    next = { ...next, tracked };
  }
  // A tracked run keeps the name the list gives it now: the page names a category better once its lookup lands (e.g.
  // telling two "Cats" apart), and its "ready" toast should read like its row.
  const renamed = scorings.flatMap((scoring) => {
    const known = next.tracked[scoring.id];
    return known && known.name !== scoring.name ? [{ ...known, name: scoring.name }] : [];
  });
  if (renamed.length > 0) {
    next = { ...next, tracked: { ...next.tracked, ...Object.fromEntries(renamed.map((known) => [known.id, known])) } };
  }

  const batch = next.uploads[benchmarkKey];
  if (!batch) {
    return next;
  }
  // Runs requested since the upload answer it, unless the user requested them here.
  const baselineMs = batch.baselineRequestedAt ? Date.parse(batch.baselineRequestedAt) : Number.NEGATIVE_INFINITY;
  const answering = scorings
    .filter((scoring) => Date.parse(scoring.requestedAt) > baselineMs && !next.requestedRuns.includes(runKey(scoring)))
    .map((scoring) => scoring.id)
    .filter((id) => !batch.scoringIds.includes(id));
  if (answering.length > 0) {
    next = {
      ...next,
      uploads: { ...next.uploads, [benchmarkKey]: { ...batch, scoringIds: [...batch.scoringIds, ...answering] } },
    };
  }
  // A run can land between two reads; the list then shows it settled without the status poll having seen it.
  for (const scoring of scorings) {
    const current = next.uploads[benchmarkKey];
    if (
      scoring.status !== 'pending' &&
      current?.scoringIds.includes(scoring.id) &&
      !current.settledIds.includes(scoring.id)
    ) {
      next = settleInBatch(next, scoring.id);
    }
  }
  return next;
}

function collectionScoringNoticesReducer(
  state: CollectionScoringNoticesState,
  action: CollectionScoringNoticesAction,
): CollectionScoringNoticesState {
  switch (action.type) {
    case 'observed':
      return observe(state, action.benchmarkKey, action.scorings);

    case 'requested': {
      const requestedRuns = [...state.requestedRuns, ...action.scorings.map(runKey)];
      // The list is read again before the request resolves, so a batch may have taken these runs for its own.
      const ids = action.scorings.map((scoring) => scoring.id);
      const uploads = Object.fromEntries(
        Object.entries(state.uploads).map(([key, batch]) => [
          key,
          { ...batch, scoringIds: batch.scoringIds.filter((id) => !ids.includes(id)) },
        ]),
      );
      return observe({ ...state, requestedRuns, uploads }, action.benchmarkKey, action.scorings);
    }

    case 'uploaded': {
      const existing = state.uploads[action.benchmarkKey];
      // A batch already announced has done its job; a new upload starts a new one.
      const batch: UploadBatch =
        existing && !existing.isAnnounced
          ? { ...existing, assetCount: existing.assetCount + action.assetCount }
          : {
              assetCount: action.assetCount,
              submittedAt: action.submittedAt,
              baselineRequestedAt: action.scorings.reduce<string | null>(
                (latest, scoring) =>
                  !latest || Date.parse(scoring.requestedAt) > Date.parse(latest) ? scoring.requestedAt : latest,
                null,
              ),
              // A run going at upload takes the new assets in; the user's own requests stay announced on their own.
              scoringIds: action.scorings
                .filter((scoring) => scoring.status === 'pending' && !state.requestedRuns.includes(runKey(scoring)))
                .map((scoring) => scoring.id),
              settledIds: [],
              imageIds: [],
              scoredImageIds: [],
              isAnnounced: false,
            };
      // The start toast counts every asset in the batch, so it matches the "scored" one.
      return notify(
        { ...state, uploads: { ...state.uploads, [action.benchmarkKey]: batch } },
        { kind: 'uploadStarted', assetCount: batch.assetCount },
      );
    }

    case 'imageUploaded': {
      const batch = state.uploads[action.benchmarkKey];
      if (!batch || batch.isAnnounced || batch.imageIds.includes(action.imageId)) {
        return state;
      }
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.benchmarkKey]: { ...batch, imageIds: [...batch.imageIds, action.imageId] },
        },
      };
    }

    case 'imagesScored': {
      const batch = state.uploads[action.benchmarkKey];
      const fresh = action.imageIds.filter((id) => batch?.imageIds.includes(id) && !batch.scoredImageIds.includes(id));
      if (!batch || fresh.length === 0) {
        return state;
      }
      const scored = { ...batch, scoredImageIds: [...batch.scoredImageIds, ...fresh] };
      return completeBatch(
        { ...state, uploads: { ...state.uploads, [action.benchmarkKey]: scored } },
        action.benchmarkKey,
      );
    }

    case 'imageFailed': {
      const batch = state.uploads[action.benchmarkKey];
      if (!batch || batch.isAnnounced) {
        return state;
      }
      const failed = {
        ...batch,
        assetCount: Math.max(batch.assetCount - 1, 0),
        imageIds: batch.imageIds.filter((id) => id !== action.imageId),
        scoredImageIds: batch.scoredImageIds.filter((id) => id !== action.imageId),
      };
      // Nothing left to score: the batch ends without a "scored" toast.
      if (failed.assetCount === 0) {
        const { [action.benchmarkKey]: _empty, ...uploads } = state.uploads;
        return { ...state, uploads };
      }
      return completeBatch(
        { ...state, uploads: { ...state.uploads, [action.benchmarkKey]: failed } },
        action.benchmarkKey,
      );
    }

    case 'settled': {
      const scoring = state.tracked[action.id];
      if (!scoring && !batchOf(state, action.id)) {
        return state;
      }
      const { [action.id]: _landed, ...tracked } = state.tracked;
      const next = { ...state, tracked };
      if (batchOf(next, action.id)) {
        return settleInBatch(next, action.id);
      }
      if (!scoring || action.status !== 'ready') {
        return next;
      }
      return notify(next, {
        kind: 'ready',
        benchmarkKey: scoring.benchmarkKey,
        categoryId: scoring.categoryId,
        name: scoring.name,
      });
    }

    case 'expired': {
      const batch = state.uploads[action.benchmarkKey];
      if (!batch || batch.submittedAt !== action.submittedAt) {
        return state;
      }
      const { [action.benchmarkKey]: _expired, ...uploads } = state.uploads;
      return { ...state, uploads };
    }

    case 'delivered':
      return state.outbox.some((notice) => notice.id <= action.throughId)
        ? { ...state, outbox: state.outbox.filter((notice) => notice.id > action.throughId) }
        : state;

    default:
      return state;
  }
}

export { INITIAL_COLLECTION_SCORING_NOTICES, collectionScoringNoticesReducer };
export type {
  CollectionScoringNotice,
  CollectionScoringNoticesAction,
  CollectionScoringNoticesState,
  ObservedScoring,
  UploadBatch,
};
